import { f7, useStore } from 'framework7-react';
import { Router } from 'framework7/types';
import { useCallback, useEffect, useState } from 'react';
import { useToast } from './useToast';
import { multipleAffiliateInfo } from '@/api/authApi';
import { customerInfoApi } from '@/api/customerApi';
import { linkDriveOwnerApi } from '@/api/infoApi';
import { AuthInfoState, initialState } from '@/config/initialState';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { affiliateId as AFFILIATE_ID } from '@/consts/shop';
import { Car } from '@/types/api/customerApi';
import { LinkDriveOwner } from '@/types/api/infoApi';
import {
  captureCustomerSession,
  isCurrentCustomerSession,
} from '@/utils/customerSession';
import { initKarte } from '@/utils/karte';
import { getLoginTelForType, getValidLoginType } from '@/utils/loginTel';

import { paymentStorage } from '@/utils/paymentStorage';
import { getThemeIdFromAffiliateId } from '@/utils/shop';
import { storeDispatch } from '@/utils/store';
import { getKeyByValue } from '@/utils/utils';
import {
  findRelevantNextWarranty,
  getPreferredCurrentWarranty,
} from '@/utils/warranty';

export const useLoadCustomer = (
  f7router: Router.Router,
  token?: string,
  customerId?: number,
  status?: string,
  isLoggedInInit?: boolean,
) => {
  const m_car_id = paymentStorage.getItem('m_car_id');
  const t_order_id = paymentStorage.getItem('t_order_id');
  const storedGlobalTheme = paymentStorage.getItem('globalTheme');
  const storedKarteUrlTheme = paymentStorage.getItem('karteUrlTheme');
  const isLoggedInToMaikuru =
    paymentStorage.getItem('isLoggedInToMaikuru') === 'true';
  const loginType = getValidLoginType(paymentStorage.getItem('loginType'));
  const loginTel = getLoginTelForType(loginType, paymentStorage);
  const [isLoggedIn, setIsLoggedIn] = useState(isLoggedInInit ?? false);
  const { openToast } = useToast();
  const authInfo = useStore(store, 'authInfo') as AuthInfoState;
  const isInfoLoaded = useStore(store, 'getIsInfoLoaded') as boolean;
  const [isFetching, setIsFetching] = useState(false);

  const isPaymentReturn =
    !!status && /^(gmo|charge_prepaid|payment)_/.test(status);

  const autoLogin = useCallback(async () => {
    // A mounted payment page must never replace an already authenticated session.
    const current = store.state.authInfo;
    if (current.isLogin && current.token) {
      if (
        isPaymentReturn &&
        (!token ||
          Number(customerId) !== current.m_customer_id ||
          token !== current.token)
      ) {
        f7router.navigate(paths.home, { animate: false, reloadAll: true });
        return;
      }
      setIsLoggedIn(true);
      return;
    }
    if (token && Number.isSafeInteger(customerId) && Number(customerId) > 0) {
      await storeDispatch('setAuthInfo', 'authInfo', {
        m_customer_id: Number(customerId),
        token,
        isLogin: true,
      });
      await storeDispatch('setLoginInfo', 'loginInfo', {
        loginType,
        tel: loginTel,
      });
      await storeDispatch(
        'setIsLoggedInToMaikuru',
        'isLoggedInToMaikuru',
        isLoggedInToMaikuru,
      );
      // loginType は次回のGMOリダイレクト復帰時にも利用するため削除しない
      // paymentStorage.removeItem('token');
      // paymentStorage.removeItem('m_customer_id');
      paymentStorage.removeItem('globalTheme');
      paymentStorage.removeItem('karteUrlTheme');
      paymentStorage.removeItem('isLoggedInToMaikuru');
      setIsLoggedIn(true);
    } else if (isPaymentReturn) {
      f7router.navigate(paths.loginTutorial, {
        animate: false,
        reloadAll: true,
      });
    }
  }, [
    customerId,
    isLoggedInToMaikuru,
    loginTel,
    loginType,
    token,
    isPaymentReturn,
    f7router,
  ]);

  const setThemeIdFromAffiliateId = async (affiliateId: number | undefined) => {
    // GMO リダイレクト後: 正規化前の URL themeId で KARTE ロックを復元
    if (storedKarteUrlTheme) {
      initKarte(Number(storedKarteUrlTheme), true);
    }
    const globalTheme =
      storedGlobalTheme && storedGlobalTheme !== 'null'
        ? Number(storedGlobalTheme)
        : null;
    if (affiliateId && getKeyByValue(AFFILIATE_ID, affiliateId)) {
      if (globalTheme) {
        await storeDispatch('setGlobalThemeId', 'themeId', globalTheme);
        initKarte(globalTheme, true);
      } else {
        const resolvedThemeId = getThemeIdFromAffiliateId(affiliateId);
        await storeDispatch('setThemeId', 'themeId', resolvedThemeId);
        initKarte(resolvedThemeId);
      }
    }
  };

  const setStoreLinkDriveOwner = async (cars?: Car[]) => {
    const session = captureCustomerSession();
    if (!cars || !session.auth.m_customer_id) return;
    try {
      const { data: linkDriveOwner } = await linkDriveOwnerApi({
        params: {
          customer_id: session.auth.m_customer_id,
          stock_car_id: cars?.[0]?.t_stock_car_id ?? 0,
          del_flg: 0,
        },
      });

      if (!isCurrentCustomerSession(session)) return;
      paymentStorage.removeItem('currentCarId');

      if (
        linkDriveOwner?.error &&
        linkDriveOwner.error.message !== 'LinkDriveOwnerなし'
      ) {
        throw new Error(linkDriveOwner.error.message);
      }
      const linkDriveOwnerData =
        linkDriveOwner?.data?.link_drive_owner ??
        ({} as Partial<LinkDriveOwner>);
      const OwnerID = linkDriveOwnerData?.owner_id;
      const SerialNo = linkDriveOwnerData?.serial_no;

      await Promise.all([
        await storeDispatch(
          'setLinkDriveOwnerInfo',
          'linkDriveOwnerInfo',
          linkDriveOwnerData,
        ),
        await storeDispatch('setOwnerId', 'ownerId', OwnerID),
        await storeDispatch('setSerialNumberDb', 'serialNumberDb', SerialNo),
      ]);
      return linkDriveOwner;
    } catch (e: unknown) {
      if (e instanceof Error) {
        console.error(e.message);
      }
    }
  };

  const setCustomerInfo = useCallback(async () => {
    const session = captureCustomerSession();
    if (!session.auth.isLogin || !session.auth.m_customer_id) return;
    try {
      const response = await customerInfoApi({
        m_customer_id: session.auth.m_customer_id,
      });
      if (!response?.data) {
        openToast('基本情報の取得に失敗しました', 'toast-failed');
        return;
      }
      if (!isCurrentCustomerSession(session)) return;
      const { data: customerInfo } = response;
      if (
        !customerInfo.success ||
        customerInfo.customer?.id !== session.auth.m_customer_id
      ) {
        throw new Error('customerInfoApi failed');
      }
      const {
        customer,
        cars,
        bill_infomations,
        payment_methods,
        prepaids,
        informations,
        campaigns,
      } = customerInfo;
      let car = cars?.[0];
      if ((m_car_id || t_order_id) && cars) {
        car =
          cars.find((i) =>
            !i.m_car_id
              ? i.t_order_id === Number(t_order_id)
              : i.m_car_id === Number(m_car_id),
          ) || cars[0];
      }
      const preferredWarranty = getPreferredCurrentWarranty(car?.warranties);
      await Promise.all([
        storeDispatch(
          'setCarItemInfo',
          'carItemInfo',
          car || initialState.carItemInfo,
        ),
        storeDispatch('setCustomerInfo', 'customerInfo', customer),
        storeDispatch('setCarsInfo', 'carsInfo', cars),
        storeDispatch('setBillsInfo', 'billsInfo', bill_infomations),
        storeDispatch(
          'setPaymentMethodsInfo',
          'paymentMethodsInfo',
          payment_methods,
        ),
        storeDispatch(
          'setAdvancePaymentsInfo',
          'advancePaymentsInfo',
          prepaids,
        ),
        storeDispatch(
          'setNotificationsInfo',
          'notificationsInfo',
          informations,
        ),
        storeDispatch('setCampaignsInfo', 'campaignsInfo', campaigns),
        storeDispatch('setWarranties', 'warranties', car?.warranties || []),
        storeDispatch(
          'setNextWarranties',
          'nextWarranties',
          car?.next_warranty || [],
        ),
        storeDispatch(
          'setCurrentWarranty',
          'currentWarranty',
          preferredWarranty,
        ),
        storeDispatch(
          'setNextWarranty',
          'nextWarranty',
          findRelevantNextWarranty(preferredWarranty, car?.next_warranty),
        ),
      ]);
      return customerInfo;
    } catch (e) {
      console.error(e);
      openToast('基本情報の取得に失敗しました', 'toast-failed');
    }
  }, [customerId, openToast]);

  const retrieveUserInfo = useCallback(async () => {
    const session = captureCustomerSession();
    try {
      f7.preloader.show();
      const customerInfo = await setCustomerInfo();
      if (!customerInfo || !isCurrentCustomerSession(session)) return;
      let parameter = '';
      if (loginType === 'email') {
        parameter = customerInfo.customer?.email || '';
      }
      if (loginType === 'telephone') {
        parameter =
          store.state.loginInfo?.tel || customerInfo.customer?.phone_no1 || '';
      }
      if (loginType === 'admin') {
        parameter =
          customerInfo?.customer?.email ||
          store.state.loginInfo?.tel ||
          customerInfo?.customer?.phone_no1 ||
          '';
      }
      if (parameter) {
        const { data: affiliatesInfo } = await multipleAffiliateInfo({
          parameter,
        });
        if (!isCurrentCustomerSession(session)) return;
        await storeDispatch(
          'setAffiliatesInfo',
          'affiliatesInfo',
          affiliatesInfo.m_customers,
        );
      }
      await storeDispatch('setIsInfoLoaded', 'isInfoLoaded', true);
      await setThemeIdFromAffiliateId(customerInfo.customer?.m_affiliate_id);
      await setStoreLinkDriveOwner(customerInfo.cars);
    } catch (e: unknown) {
      console.error(e);
    } finally {
      setIsFetching(false);
      f7.preloader.hide();
    }
  }, [setCustomerInfo, loginType]);

  const resetIsLoggedIn = () => setIsLoggedIn(false);

  useEffect(() => {
    if (!isLoggedIn) {
      autoLogin();
    }
  }, [autoLogin, isLoggedIn, status]);

  useEffect(() => {
    if (!authInfo.isLogin) return;
    if (isInfoLoaded) return;
    if (status) return;
    if (!isFetching) {
      setIsFetching(true);
      void retrieveUserInfo();
    }
  }, [authInfo, isInfoLoaded]);

  return {
    isLoggedIn,
    resetIsLoggedIn,
    retrieveUserInfo,
  };
};
